// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The geometry validation classes of 04 §16 (M2-21).
//
// Everything in `validate.ts` asks whether the document is well formed. Everything here asks
// the harder question: **is the thing it describes a pinball machine?** A definition can be
// perfectly typed, every number inside its range, every reference resolved, and still describe
// a pocket the ball rolls into and never leaves.
//
// Five classes live here:
//
//  * **overlap** — no two static colliders interpenetrate.
//  * **flipper crossing** — PH-054, the inequality that decides whether the bats collide and
//    whether a centre drain exists at all.
//  * **trap** — a place the ball can reach but cannot leave, found by flood-fill at ball radius.
//  * **reachability** — every scoring device is reachable from where the ball can go.
//  * **layer integrity** — every ramp climbs at the calibrated shot speed, fits under the
//    glass, and leads somewhere that eventually returns the ball to a plane.
//
// **What the flood fill models, and what it deliberately does not.** It works on the plane
// layer only, at exact ball radius, and it treats reachability as *undirected* except across
// one-way gates and kicker ejects. It has no gravity. That is on purpose: a pinball has energy
// and routinely travels uphill, so a gravity-directed fill would report half a real machine as
// unreachable. The consequence is that this pass finds the traps that are traps for everyone —
// sealed pockets, and pockets whose only door is a gate pointing the wrong way — and does not
// pretend to find "the ball can technically get out but never does". That one is the soak's
// job (04 §16, Playability), and the soak is the reason this pass does not have to be clever.
//
// Flippers are **not** solid here. A bat at rest can close a channel that opens the instant the
// player flips, so treating it as a wall invents traps that do not exist.

import { BALL_DIAMETER, BALL_RADIUS, G } from '../sim/constants'
import { Path } from '../sim/Path'
import { abs, atan2, cos, floor, sin, sqrt, TWO_PI } from '../math/deterministic'
import type {
  MachineDefinition,
  MachineDevice,
  MachineLayer,
  Point2,
} from './types'

export interface GeometryIssue {
  readonly path: string
  readonly code: string
  readonly message: string
  readonly severity: 'error' | 'warning'
}

/**
 * The speed a ball leaves a full-strength flipper at, m/s.
 *
 * The low end of the measured band in PH-A07 (5.80–6.40 m/s), because a ramp that only climbs
 * on the machine's best shot is not a ramp the player can use. **[VERIFY]** — it moves if the
 * calibration harness's band moves.
 */
export const CALIBRATED_SHOT_SPEED = 5.8

/**
 * Fraction of entry kinetic energy that survives the climb.
 *
 * **Calibrated backwards from a real machine rather than guessed, because the guess was
 * useless.** The first version used 0.55 on the reasoning that a ramp is fairly efficient. It
 * is not, and worse, at 0.55 no ramp the format can even express would fail: the ceiling came
 * out at 1.32 m, against a spline `z` the schema caps at 0.4. The check could never fire.
 *
 * A real 60 mm ramp needs roughly 3.0 m/s at its entry. A rolling ball carries `(7/10)·v²` per
 * kilogram, so the surviving fraction is `(10·g·0.06/7) / 3.0²` ≈ 0.093 — the lip, both walls
 * and the spin-up against a new surface take about nine tenths of it. Rounded to 0.10, the
 * ceiling at a full-strength shot is 240 mm, which is well above any real ramp (they top out
 * near 150 mm) and well inside what the format permits, so the check both passes real machines
 * and can actually catch one. **[VERIFY against a built ramp.]**
 */
export const RAMP_ENERGY_EFFICIENCY = 0.1

/**
 * How finely a ramp is sampled when measuring its apex.
 *
 * The same sixteen `Path` uses by default and the Builder's profile uses, so the three agree about
 * how tall a ramp is. A coarser sampling here would let a bulge slip between samples, which is the
 * fault this sampling exists to catch.
 */
const APEX_SAMPLES_PER_SEGMENT = 16

/** Interpenetration below this is contact, not overlap. 0.1 mm. */
const OVERLAP_TOLERANCE = 1e-4

/**
 * Flood-fill cell size, metres.
 *
 * 2.5 mm against a 27 mm ball: a channel one ball wide is about eleven cells, so a genuinely
 * open lane is unmistakably open and a channel narrower than the ball has no free cells at all.
 */
const CELL = 0.0025

const KIND_SEGMENT = 0
const KIND_CIRCLE = 1
const KIND_ARC = 2

interface Solid {
  readonly path: string
  readonly kind: number
  /** Segment endpoints, or the centre for a circle or arc. */
  readonly ax: number
  readonly ay: number
  readonly bx: number
  readonly by: number
  readonly r: number
  readonly from: number
  readonly to: number
  /** Arc only: true excludes the ball from the disc, false contains it inside. */
  readonly insideOut: boolean
  /** Whether this solid takes part in the free-space fill. Gates and flippers do not. */
  readonly blocks: boolean
}

function seg(path: string, a: Point2, b: Point2, blocks = true): Solid {
  return { path, kind: KIND_SEGMENT, ax: a[0], ay: a[1], bx: b[0], by: b[1], r: 0, from: 0, to: 0, insideOut: false, blocks }
}

function disc(path: string, c: Point2, r: number, blocks = true): Solid {
  return { path, kind: KIND_CIRCLE, ax: c[0], ay: c[1], bx: 0, by: 0, r, from: 0, to: 0, insideOut: false, blocks }
}

// --- primitive distances -------------------------------------------------------

function distPointSegment(px: number, py: number, s: Solid): number {
  const dx = s.bx - s.ax
  const dy = s.by - s.ay
  const len2 = dx * dx + dy * dy
  let t = len2 === 0 ? 0 : ((px - s.ax) * dx + (py - s.ay) * dy) / len2
  if (t < 0) t = 0
  else if (t > 1) t = 1
  const qx = s.ax + t * dx - px
  const qy = s.ay + t * dy - py
  return sqrt(qx * qx + qy * qy)
}

/** Is `angle` inside the arc's span? Spans are stored normalised to a positive sweep. */
function angleInSpan(angle: number, from: number, to: number): boolean {
  let a = angle - from
  while (a < 0) a += TWO_PI
  while (a >= TWO_PI) a -= TWO_PI
  let span = to - from
  while (span < 0) span += TWO_PI
  while (span > TWO_PI) span -= TWO_PI
  return a <= span
}

/**
 * Signed clearance for a point against a solid: negative means the ball centre is inside it.
 *
 * One function for all three shapes so the fill has a single branch and the meaning of
 * "blocked" cannot drift between them.
 */
function clearance(px: number, py: number, s: Solid): number {
  if (s.kind === KIND_SEGMENT) return distPointSegment(px, py, s) - BALL_RADIUS
  const dx = px - s.ax
  const dy = py - s.ay
  const d = sqrt(dx * dx + dy * dy)
  if (s.kind === KIND_CIRCLE) return d - (s.r + BALL_RADIUS)
  // An arc only constrains the ball across its own angular span.
  if (!angleInSpan(atan2(dy, dx), s.from, s.to)) return 1
  return s.insideOut ? d - (s.r + BALL_RADIUS) : s.r - BALL_RADIUS - d
}

// --- gathering the solids -------------------------------------------------------

/**
 * Every piece of static geometry on the playfield plane, as a flat list.
 *
 * Devices contribute here too. A slingshot's face and a drop target's face are as solid as a
 * wall, and a validator that only looked at the `colliders` array would happily accept a post
 * driven through the middle of a target.
 */
function gatherSolids(def: MachineDefinition): Solid[] {
  const planeId = def.layers.find((l) => l.kind === 'plane')?.id
  const out: Solid[] = []

  for (let i = 0; i < def.colliders.length; i++) {
    const c = def.colliders[i]!
    if (c.layer !== planeId) continue
    const path = `colliders[${i}]`
    if (c.kind === 'segment') {
      out.push(seg(path, c.a, c.b))
    } else if (c.kind === 'circle') {
      out.push(disc(path, c.centre, c.radius))
    } else if (c.kind === 'polyline') {
      for (let p = 0; p + 1 < c.points.length; p++) {
        out.push(seg(`${path}.points[${p}..${p + 1}]`, c.points[p]!, c.points[p + 1]!))
      }
    } else {
      out.push({
        path,
        kind: KIND_ARC,
        ax: c.centre[0],
        ay: c.centre[1],
        bx: 0,
        by: 0,
        r: c.radius,
        from: c.angleFrom,
        to: c.angleTo,
        insideOut: c.insideOut,
        blocks: true,
      })
    }
  }

  for (let i = 0; i < def.devices.length; i++) {
    const d = def.devices[i]!
    if (d.layer !== undefined && d.layer !== planeId) continue
    const path = `devices[${i}]`
    switch (d.kind) {
      case 'slingshot':
      case 'dropTarget':
      case 'standupTarget':
      case 'spinner':
        out.push(seg(path, d.a, d.b))
        break
      case 'gate':
      case 'ballSaveGate':
      case 'diverter':
        // Solid for overlap — two gates through each other is still a bug — but not for the
        // fill, which handles a gate as a one-way door rather than as a wall.
        out.push(seg(path, d.a, d.b, false))
        break
      case 'popBumper':
        out.push(disc(path, d.centre, d.skirtRadius))
        break
      case 'captiveBall':
        out.push(disc(path, d.centre, BALL_RADIUS))
        break
      default:
        break
    }
  }
  return out
}

// --- class: overlap ---------------------------------------------------------------

/**
 * Which side of a → b the point p is on, with a tolerance measured in **metres**.
 *
 * The cross product is divided by the segment's own length, which matters more than it looks:
 * unnormalised, a fixed tolerance means one thing for a 500 mm wall and something a thousand
 * times stricter for a 2.5 mm grid step. That is not hypothetical — the first version of the
 * flood fill silently leaked through every one-way gate, because a grid edge's cross product
 * against a gate face came out at 3.5e-5 and the tolerance was 1e-4, so the edge read as
 * "touching" rather than "crossing" and the gate was ignored.
 */
function side(ax: number, ay: number, bx: number, by: number, px: number, py: number, tol: number): number {
  const dx = bx - ax
  const dy = by - ay
  const len = sqrt(dx * dx + dy * dy)
  if (len === 0) return 0
  const v = (dx * (py - ay) - dy * (px - ax)) / len
  if (v > tol) return 1
  if (v < -tol) return -1
  return 0
}

function segmentsCross(a: Solid, b: Solid, tol: number): boolean {
  const d1 = side(a.ax, a.ay, a.bx, a.by, b.ax, b.ay, tol)
  const d2 = side(a.ax, a.ay, a.bx, a.by, b.bx, b.by, tol)
  const d3 = side(b.ax, b.ay, b.bx, b.by, a.ax, a.ay, tol)
  const d4 = side(b.ax, b.ay, b.bx, b.by, a.bx, a.by, tol)
  return d1 !== 0 && d2 !== 0 && d3 !== 0 && d4 !== 0 && d1 !== d2 && d3 !== d4
}

/** Does a segment cross an arc's curve transversally? Tangency is a touch, not a crossing. */
function segmentCrossesArc(s: Solid, arc: Solid): boolean {
  const dx = s.bx - s.ax
  const dy = s.by - s.ay
  const fx = s.ax - arc.ax
  const fy = s.ay - arc.ay
  const a = dx * dx + dy * dy
  if (a === 0) return false
  const b = 2 * (fx * dx + fy * dy)
  const c = fx * fx + fy * fy - arc.r * arc.r
  const disc2 = b * b - 4 * a * c
  // A grazing root pair is a tangency; only a clean pair of separated roots is a crossing.
  if (disc2 <= OVERLAP_TOLERANCE) return false
  const root = sqrt(disc2)
  for (const t of [(-b - root) / (2 * a), (-b + root) / (2 * a)]) {
    if (t <= 1e-6 || t >= 1 - 1e-6) continue
    const px = s.ax + t * dx - arc.ax
    const py = s.ay + t * dy - arc.ay
    if (angleInSpan(atan2(py, px), arc.from, arc.to)) return true
  }
  return false
}

function checkOverlap(solids: readonly Solid[], issues: GeometryIssue[]): void {
  for (let i = 0; i < solids.length; i++) {
    const a = solids[i]!
    for (let j = i + 1; j < solids.length; j++) {
      const b = solids[j]!
      // Two pieces of the same polyline meet at a shared endpoint by construction.
      if (a.path === b.path) continue
      let hit = false
      if (a.kind === KIND_SEGMENT && b.kind === KIND_SEGMENT) {
        hit = segmentsCross(a, b, OVERLAP_TOLERANCE)
      } else if (a.kind === KIND_CIRCLE && b.kind === KIND_CIRCLE) {
        const dx = b.ax - a.ax
        const dy = b.ay - a.ay
        hit = sqrt(dx * dx + dy * dy) < a.r + b.r - OVERLAP_TOLERANCE
      } else if (a.kind === KIND_CIRCLE && b.kind === KIND_SEGMENT) {
        hit = distPointSegment(a.ax, a.ay, b) < a.r - OVERLAP_TOLERANCE
      } else if (a.kind === KIND_SEGMENT && b.kind === KIND_CIRCLE) {
        hit = distPointSegment(b.ax, b.ay, a) < b.r - OVERLAP_TOLERANCE
      } else if (a.kind === KIND_ARC && b.kind === KIND_SEGMENT) {
        hit = segmentCrossesArc(b, a)
      } else if (a.kind === KIND_SEGMENT && b.kind === KIND_ARC) {
        hit = segmentCrossesArc(a, b)
      }
      // Arc against arc and arc against circle are not tested. Said plainly rather than
      // implied: two curved walls through each other would not be caught here.
      if (hit) {
        issues.push({
          path: a.path,
          code: 'colliders-overlap',
          message: `${a.path} interpenetrates ${b.path}; two solid surfaces cannot occupy the same space`,
          severity: 'error',
        })
      }
    }
  }
}

// --- class: flipper crossing (PH-054) ------------------------------------------------

/**
 * One opposing pair of flippers, measured against PH-054 (M9-06).
 *
 * **Both inequalities, with their terms kept separately**, because 11 §3.1 asks for a readout that
 * prints them: `L·cos α ≤ p − (r_t + R)` for a passable centre drain, and `L·cos α < p − r_t` for
 * merely not interpenetrating. A verdict on its own — "too long" — is the thing that made this trap
 * expensive the first time; the numbers beside the inequality are what make it obvious.
 *
 * **The verdict comes from the tip positions, not from the terms.** `L·cos α` assumes the bats point
 * inwards and that the rest angle uses one particular sign convention; the gap between the resting
 * tips assumes neither, and is what the ball actually meets. On a symmetric machine the two agree
 * exactly. On an asymmetric one the gap is the honest answer, and it is the one the publish check
 * has always used.
 */
export interface FlipperPairGeometry {
  readonly leftId: string
  readonly rightId: string
  /** Index into `def.devices`, so a caller can select the offending bat. */
  readonly leftIndex: number
  readonly rightIndex: number
  /** Half the pivot separation, metres. PH-054's `p`. */
  readonly p: number
  /** Each bat's horizontal reach at rest — PH-054's `L·cos α`, measured from the tip. */
  readonly reachLeft: number
  readonly reachRight: number
  /** PH-054's `r_t`, per side. */
  readonly tipRadiusLeft: number
  readonly tipRadiusRight: number
  /** The clear space between the resting tips, metres. Negative means they overlap. */
  readonly gap: number
  /** `L·cos α < p − r_t` — the bats do not touch. */
  readonly clears: boolean
  /** `L·cos α ≤ p − (r_t + R)` — a ball fits between them. */
  readonly drains: boolean
}

/**
 * Every opposing flipper pair in a machine, measured.
 *
 * A pair is one left and one right bat whose pivots sit at the same height, which is what makes them
 * a pair rather than two flippers that happen to exist. The publish check and the Builder's readout
 * both read this, so they cannot disagree about a machine.
 */
export function flipperPairs(def: MachineDefinition): readonly FlipperPairGeometry[] {
  const flippers: { index: number; d: Extract<MachineDevice, { kind: 'flipper' }> }[] = []
  for (let i = 0; i < def.devices.length; i++) {
    const d = def.devices[i]!
    if (d.kind === 'flipper') flippers.push({ index: i, d })
  }

  const pairs: FlipperPairGeometry[] = []
  for (let i = 0; i < flippers.length; i++) {
    for (let j = i + 1; j < flippers.length; j++) {
      const A = flippers[i]!
      const B = flippers[j]!
      // Opposing bats: one left, one right, pivots at the same height.
      if (A.d.handedness === B.d.handedness) continue
      if (abs(A.d.pivot[1] - B.d.pivot[1]) > 0.01) continue
      const left = A.d.pivot[0] < B.d.pivot[0] ? A : B
      const right = A.d.pivot[0] < B.d.pivot[0] ? B : A

      // Worked from the resting tip positions rather than from `L·cos α`, so the check does
      // not have to assume anything about which sign convention the rest angle uses.
      const leftTip = left.d.pivot[0] + left.d.length * cos(left.d.restAngle)
      const rightTip = right.d.pivot[0] + right.d.length * cos(right.d.restAngle)
      const gap = rightTip - right.d.radiusTip - (leftTip + left.d.radiusTip)

      pairs.push({
        leftId: left.d.id,
        rightId: right.d.id,
        leftIndex: left.index,
        rightIndex: right.index,
        p: (right.d.pivot[0] - left.d.pivot[0]) / 2,
        reachLeft: abs(leftTip - left.d.pivot[0]),
        reachRight: abs(rightTip - right.d.pivot[0]),
        tipRadiusLeft: left.d.radiusTip,
        tipRadiusRight: right.d.radiusTip,
        gap,
        clears: gap > 0,
        drains: gap >= BALL_DIAMETER,
      })
    }
  }
  return pairs
}

function checkFlipperPairs(
  def: MachineDefinition,
  solids: readonly Solid[],
  issues: GeometryIssue[],
): void {
  // The pairs are found and measured once, by the function the Builder's readout also calls, so a
  // machine cannot be described one way on a canvas and another way at publish.
  for (const pair of flipperPairs(def)) {
    {
      const left = {
        index: pair.leftIndex,
        d: def.devices[pair.leftIndex] as Extract<MachineDevice, { kind: 'flipper' }>,
      }
      const right = {
        index: pair.rightIndex,
        d: def.devices[pair.rightIndex] as Extract<MachineDevice, { kind: 'flipper' }>,
      }
      const gap = pair.gap

      if (gap <= 0) {
        issues.push({
          path: `devices[${left.index}]`,
          code: 'flippers-interpenetrate',
          message:
            `the resting bats of "${left.d.id}" and "${right.d.id}" overlap by ${(-gap).toFixed(5)} m. ` +
            'PH-054 requires L·cos(α) < p − r_t: shorten the bat, widen the pivots, or lower the rest angle.',
          severity: 'error',
        })
      } else if (gap < BALL_DIAMETER) {
        issues.push({
          path: `devices[${left.index}]`,
          code: 'no-centre-drain',
          message:
            `the centre gap between "${left.d.id}" and "${right.d.id}" is ${(gap * 1000).toFixed(1)} mm, ` +
            `narrower than the ball's ${(BALL_DIAMETER * 1000).toFixed(1)} mm. Legal, but the ball can ` +
            'only ever drain through the outlanes, which removes the nudge save from the machine.',
          severity: 'warning',
        })
      }

      // The pair that makes the centre drain is the pair that guards it, so this is where the
      // outboard check belongs. An upper flipper has open playfield beside it by design: a
      // ball going past one carries on down the table rather than out of the game.
      checkFlipperOutboard(solids, left.index, left.d, -1, issues)
      checkFlipperOutboard(solids, right.index, right.d, 1, issues)
    }
  }
}

// --- class: the flipper's outboard side ------------------------------------------------

/**
 * Distance from a point to a solid's surface, unsigned.
 *
 * `clearance` is written for a ball centre and so has the radius already taken off; adding it
 * back gives the plain geometric distance, for all three shapes and without a second branch.
 */
function surfaceDistance(px: number, py: number, s: Solid): number {
  return clearance(px, py, s) + BALL_RADIUS
}

/** Walk a solid's outline at roughly `SAMPLE` spacing, calling `visit` at each point. */
const SAMPLE = 0.002

function sampleSolid(s: Solid, visit: (x: number, y: number) => void): void {
  if (s.kind === KIND_SEGMENT) {
    const dx = s.bx - s.ax
    const dy = s.by - s.ay
    const n = 1 + floor(sqrt(dx * dx + dy * dy) / SAMPLE)
    for (let i = 0; i <= n; i++) visit(s.ax + (dx * i) / n, s.ay + (dy * i) / n)
    return
  }
  let from = 0
  let span = TWO_PI
  if (s.kind === KIND_ARC) {
    from = s.from
    span = s.to - s.from
    while (span < 0) span += TWO_PI
    while (span > TWO_PI) span -= TWO_PI
  }
  const n = 1 + floor((span * s.r) / SAMPLE)
  for (let i = 0; i <= n; i++) {
    const a = from + (span * i) / n
    visit(s.ax + s.r * cos(a), s.ay + s.r * sin(a))
  }
}

/**
 * The gap between a flipper and whatever stands outboard of it (04 §16, Geometry).
 *
 * This is the class that was missing, and the defect it exists for cost a quarter of every
 * drain in the flagship machine. Each inlane guide ended 45.5 mm from its flipper pivot,
 * leaving 34.5 mm of clear air against a 27.0 mm ball, and balls fed down the inlane — the
 * single most common delivery in pinball — fell past the flipper instead of onto it.
 *
 * Nothing else caught it. The definition validated, the flood fill found no trap (the hole
 * drained perfectly well, which is exactly the problem), the soak reported no stuck ball and
 * no escape, and every rule fired. The machine was unplayable and every test agreed it was
 * fine.
 *
 * The rule is the one an operator would state: **something must stand within a ball's width
 * of the flipper's base, on its outboard side.** On a real machine that something is the end
 * of the inlane guide, a few millimetres off the base rubber. Measured from the pivot rather
 * than modelled as a falling ball, because the first attempt at this check did model a
 * falling ball and passed the very geometry it was written for — the slingshot's underside
 * happened to clip the top of the sampled column, so the column read as closed when the ball
 * was in fact arriving along the inlane from the side and dropping straight through.
 */
function checkFlipperOutboard(
  solids: readonly Solid[],
  index: number,
  f: Extract<MachineDevice, { kind: 'flipper' }>,
  sign: number,
  issues: GeometryIssue[],
): void {
  // Outboard of the pivot, and in the band a ball arriving at the flipper occupies. The lower
  // bound keeps the apron out of it — geometry below the flipper cannot stop a ball going
  // past the flipper — and the upper bound keeps the upper playfield out.
  const yLow = f.pivot[1] - BALL_DIAMETER
  const yHigh = f.pivot[1] + BALL_DIAMETER * 4
  let nearest = Number.POSITIVE_INFINITY
  for (const s of solids) {
    if (!s.blocks) continue
    // Cheap reject: nothing whose surface is already further than the best can beat it.
    if (surfaceDistance(f.pivot[0], f.pivot[1], s) - SAMPLE > nearest) continue
    sampleSolid(s, (x, y) => {
      if ((x - f.pivot[0]) * sign <= 0) return
      if (y < yLow || y > yHigh) return
      const dx = x - f.pivot[0]
      const dy = y - f.pivot[1]
      const d = sqrt(dx * dx + dy * dy)
      if (d < nearest) nearest = d
    })
  }

  const gap = nearest - f.radiusPivot
  if (gap < BALL_DIAMETER) return
  const shown = Number.isFinite(gap) ? `${(gap * 1000).toFixed(1)} mm of clear air` : 'nothing at all'
  issues.push({
    path: `devices[${index}]`,
    code: 'flipper-outboard-gap',
    message:
      `there is ${shown} outboard of "${f.id}"'s base, against a ` +
      `${(BALL_DIAMETER * 1000).toFixed(1)} mm ball, so a ball fed down the inlane falls past the ` +
      'flipper instead of onto it. End the inlane guide within a ball of the flipper base — on a ' +
      'real machine it stops a few millimetres off the base rubber.',
    severity: 'error',
  })
}

// --- classes: trap and reachability ------------------------------------------------

interface Grid {
  readonly cols: number
  readonly rows: number
  readonly minX: number
  readonly minY: number
  /** 1 where the ball centre cannot be. */
  readonly blocked: Uint8Array
}

function cellCentreX(g: Grid, col: number): number {
  return g.minX + (col + 0.5) * CELL
}
function cellCentreY(g: Grid, row: number): number {
  return g.minY + (row + 0.5) * CELL
}
function cellAt(g: Grid, x: number, y: number): number {
  const col = floor((x - g.minX) / CELL)
  const row = floor((y - g.minY) / CELL)
  if (col < 0 || row < 0 || col >= g.cols || row >= g.rows) return -1
  return row * g.cols + col
}

/** Axis-aligned bounds of a solid, already grown by the ball radius. */
function solidBounds(s: Solid): readonly [number, number, number, number] {
  if (s.kind === KIND_SEGMENT) {
    const minX = (s.ax < s.bx ? s.ax : s.bx) - BALL_RADIUS
    const maxX = (s.ax > s.bx ? s.ax : s.bx) + BALL_RADIUS
    const minY = (s.ay < s.by ? s.ay : s.by) - BALL_RADIUS
    const maxY = (s.ay > s.by ? s.ay : s.by) + BALL_RADIUS
    return [minX, minY, maxX, maxY]
  }
  const r = s.r + BALL_RADIUS
  return [s.ax - r, s.ay - r, s.ax + r, s.ay + r]
}

/**
 * Rasterise the free space the ball's *centre* may occupy.
 *
 * Solids are bucketed by their bounds first. Without that this is 90,000 cells against every
 * collider in the machine, which at NEON NOVA's size is tens of millions of distance
 * computations on every load.
 */
function buildGrid(def: MachineDefinition, solids: readonly Solid[]): Grid {
  const halfWidth = def.dimensions.playfield.width / 2
  const halfLength = def.dimensions.playfield.length / 2
  const cols = Math.ceil((halfWidth * 2) / CELL)
  const rows = Math.ceil((halfLength * 2) / CELL)
  const grid: Grid = { cols, rows, minX: -halfWidth, minY: -halfLength, blocked: new Uint8Array(cols * rows) }

  // An arc that contains the ball constrains cells across its whole disc, so it is bucketed
  // by the disc rather than by the curve; everything else is bucketed tightly.
  for (const s of solids) {
    if (!s.blocks) continue
    const [minX, minY, maxX, maxY] = solidBounds(s)
    const c0 = Math.max(0, floor((minX - grid.minX) / CELL))
    const c1 = Math.min(cols - 1, floor((maxX - grid.minX) / CELL))
    const r0 = Math.max(0, floor((minY - grid.minY) / CELL))
    const r1 = Math.min(rows - 1, floor((maxY - grid.minY) / CELL))
    if (s.kind === KIND_ARC && !s.insideOut) {
      // A containing arc blocks everything *outside* it, which its own bounds do not cover.
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const i = row * cols + col
          if (grid.blocked[i] === 1) continue
          if (clearance(cellCentreX(grid, col), cellCentreY(grid, row), s) < 0) grid.blocked[i] = 1
        }
      }
      continue
    }
    for (let row = r0; row <= r1; row++) {
      for (let col = c0; col <= c1; col++) {
        const i = row * cols + col
        if (grid.blocked[i] === 1) continue
        if (clearance(cellCentreX(grid, col), cellCentreY(grid, row), s) < 0) grid.blocked[i] = 1
      }
    }
  }

  // The cabinet itself. The bottom row is deliberately left open — that is the drain.
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = cellCentreX(grid, col)
      const y = cellCentreY(grid, row)
      if (abs(x) > halfWidth - BALL_RADIUS || y > halfLength - BALL_RADIUS) {
        grid.blocked[row * cols + col] = 1
      }
    }
  }
  return grid
}

interface Teleport {
  /** Cells the ball is taken from. */
  readonly fromX: number
  readonly fromY: number
  readonly radius: number
  /** The cell it is put down in, from `ejectLanding`. */
  readonly toCell: number
}

interface GateEdge {
  readonly ax: number
  readonly ay: number
  readonly bx: number
  readonly by: number
  readonly px: number
  readonly py: number
}

/** Breadth-first fill over free cells, honouring one-way gates and coil ejects. */
function fill(
  grid: Grid,
  seeds: readonly number[],
  gates: readonly GateEdge[],
  teleports: readonly Teleport[],
  reverse: boolean,
): Uint8Array {
  const seen = new Uint8Array(grid.cols * grid.rows)
  const queue: number[] = []
  for (const s of seeds) {
    if (s >= 0 && grid.blocked[s] === 0 && seen[s] === 0) {
      seen[s] = 1
      queue.push(s)
    }
  }

  // Only cells near a gate need the crossing test; everything else takes the fast path.
  const nearGate = new Set<number>()
  for (const g of gates) {
    const minX = (g.ax < g.bx ? g.ax : g.bx) - CELL * 2
    const maxX = (g.ax > g.bx ? g.ax : g.bx) + CELL * 2
    const minY = (g.ay < g.by ? g.ay : g.by) - CELL * 2
    const maxY = (g.ay > g.by ? g.ay : g.by) + CELL * 2
    for (let row = Math.max(0, floor((minY - grid.minY) / CELL)); row <= Math.min(grid.rows - 1, floor((maxY - grid.minY) / CELL)); row++) {
      for (let col = Math.max(0, floor((minX - grid.minX) / CELL)); col <= Math.min(grid.cols - 1, floor((maxX - grid.minX) / CELL)); col++) {
        nearGate.add(row * grid.cols + col)
      }
    }
  }

  let head = 0
  while (head < queue.length) {
    const at = queue[head++]!
    const col = at % grid.cols
    const row = (at - col) / grid.cols
    const x = cellCentreX(grid, col)
    const y = cellCentreY(grid, row)

    for (let d = 0; d < 4; d++) {
      const nc = col + (d === 0 ? 1 : d === 1 ? -1 : 0)
      const nr = row + (d === 2 ? 1 : d === 3 ? -1 : 0)
      if (nc < 0 || nr < 0 || nc >= grid.cols || nr >= grid.rows) continue
      const next = nr * grid.cols + nc
      if (seen[next] === 1 || grid.blocked[next] === 1) continue

      if (nearGate.has(at) || nearGate.has(next)) {
        const nx = cellCentreX(grid, nc)
        const ny = cellCentreY(grid, nr)
        let refused = false
        for (const g of gates) {
          const edge = seg('', [x, y], [nx, ny])
          const face = seg('', [g.ax, g.ay], [g.bx, g.by])
          // Exact: a cell centre lying precisely on a gate face is measure-zero, and any
          // slack here is a hole in the gate rather than a nicety.
          if (!segmentsCross(edge, face, 0)) continue
          // Travel is permitted only along the gate's pass direction; in the reverse fill the
          // question is whether the *forward* step next → at would have been permitted.
          const dot = reverse
            ? (x - nx) * g.px + (y - ny) * g.py
            : (nx - x) * g.px + (ny - y) * g.py
          if (dot <= 0) refused = true
        }
        if (refused) continue
      }

      seen[next] = 1
      queue.push(next)
    }

    // Coil ejects. A scoop is a pocket the ball cannot roll out of and is not a trap, because
    // the machine picks it up and throws it — so the eject is an edge in this graph.
    for (const t of teleports) {
      if (reverse) {
        if (at !== t.toCell) continue
        stampDisc(grid, seen, queue, t.fromX, t.fromY, t.radius)
      } else {
        const dx = x - t.fromX
        const dy = y - t.fromY
        if (dx * dx + dy * dy > t.radius * t.radius) continue
        if (seen[t.toCell] === 0 && grid.blocked[t.toCell] === 0) {
          seen[t.toCell] = 1
          queue.push(t.toCell)
        }
      }
    }
  }
  return seen
}

function stampDisc(grid: Grid, seen: Uint8Array, queue: number[], cx: number, cy: number, radius: number): void {
  const c0 = Math.max(0, floor((cx - radius - grid.minX) / CELL))
  const c1 = Math.min(grid.cols - 1, floor((cx + radius - grid.minX) / CELL))
  const r0 = Math.max(0, floor((cy - radius - grid.minY) / CELL))
  const r1 = Math.min(grid.rows - 1, floor((cy + radius - grid.minY) / CELL))
  for (let row = r0; row <= r1; row++) {
    for (let col = c0; col <= c1; col++) {
      const i = row * grid.cols + col
      if (seen[i] === 1 || grid.blocked[i] === 1) continue
      const dx = cellCentreX(grid, col) - cx
      const dy = cellCentreY(grid, row) - cy
      if (dx * dx + dy * dy > radius * radius) continue
      seen[i] = 1
      queue.push(i)
    }
  }
}

/** How far along its exit ray an eject is followed before giving up, metres. */
const EJECT_REACH = 0.3

/**
 * Where a coil eject puts the ball down: along the exit ray, as far as it gets.
 *
 * Two decisions are baked in here and both are deliberate.
 *
 * **The ray stops at the first wall.** So a coil aimed into the side of its own pocket lands
 * the ball back where it started, and the pocket is still reported as a trap — which is right,
 * because that is what the machine would do.
 *
 * **The ray is not stopped by a one-way gate.** A solenoid throwing a ball at three metres a
 * second pushes a hinged flap aside; a ball rolling downhill into it does not. Modelling the
 * gate as absolute here would report every scoop sitting behind an entry gate — the standard
 * way to build a ball lock — as a trap.
 *
 * Returns −1 if the ray is blocked before the ball even clears the capture radius, which means
 * the coil fires it straight into a wall.
 */
function ejectLanding(grid: Grid, centre: Point2, direction: Point2, captureRadius: number): number {
  const len = sqrt(direction[0] * direction[0] + direction[1] * direction[1])
  if (len === 0) return -1
  const ux = direction[0] / len
  const uy = direction[1] / len
  const step = CELL / 2
  let landed = -1
  for (let t = step; t <= EJECT_REACH; t += step) {
    const cell = cellAt(grid, centre[0] + ux * t, centre[1] + uy * t)
    if (cell < 0 || grid.blocked[cell] === 1) break
    if (t > captureRadius) landed = cell
  }
  return landed
}

/** Devices worth reaching. A gate or a plunger is machinery; these are the reasons to shoot. */
function isScoringDevice(kind: string): boolean {
  return (
    kind === 'popBumper' ||
    kind === 'slingshot' ||
    kind === 'standupTarget' ||
    kind === 'dropTarget' ||
    kind === 'spinner' ||
    kind === 'kicker' ||
    kind === 'rollover' ||
    kind === 'captiveBall' ||
    kind === 'magnet'
  )
}

function devicePosition(d: MachineDevice): readonly [number, number] {
  switch (d.kind) {
    case 'popBumper':
    case 'kicker':
    case 'plunger':
    case 'autoPlunger':
    case 'magnet':
    case 'captiveBall':
      return [d.centre[0], d.centre[1]]
    case 'slingshot':
    case 'dropTarget':
    case 'standupTarget':
    case 'spinner':
    case 'gate':
    case 'ballSaveGate':
    case 'diverter':
      return [(d.a[0] + d.b[0]) / 2, (d.a[1] + d.b[1]) / 2]
    case 'rollover':
      return d.region.shape === 'circle'
        ? [d.region.centre[0], d.region.centre[1]]
        : [(d.region.min[0] + d.region.max[0]) / 2, (d.region.min[1] + d.region.max[1]) / 2]
    case 'flipper':
      return [d.pivot[0], d.pivot[1]]
    default:
      return [0, 0]
  }
}

/**
 * The nearest free cell inside `radius` of a point, or −1. A device face is itself solid.
 *
 * `mask` restricts the search to cells the mask marks, which is what makes the difference
 * between "the cell nearest this device is reachable" and "the ball can get beside this
 * device". Those are not the same question, and the first one has a sharp edge: a slingshot
 * body is a triangle of line segments, and a triangle whose inradius exceeds the ball radius
 * encloses free cells that no ball can ever occupy. Ask the first question and a slingshot
 * can be reported unreachable because the phantom cavity inside its own plastic happened to
 * be a millimetre nearer than the playfield outside it.
 */
function nearestFree(grid: Grid, x: number, y: number, radius: number, mask?: Uint8Array): number {
  let best = -1
  let bestD2 = radius * radius
  const c0 = Math.max(0, floor((x - radius - grid.minX) / CELL))
  const c1 = Math.min(grid.cols - 1, floor((x + radius - grid.minX) / CELL))
  const r0 = Math.max(0, floor((y - radius - grid.minY) / CELL))
  const r1 = Math.min(grid.rows - 1, floor((y + radius - grid.minY) / CELL))
  for (let row = r0; row <= r1; row++) {
    for (let col = c0; col <= c1; col++) {
      const i = row * grid.cols + col
      if (grid.blocked[i] === 1) continue
      if (mask !== undefined && mask[i] !== 1) continue
      const dx = cellCentreX(grid, col) - x
      const dy = cellCentreY(grid, row) - y
      const d2 = dx * dx + dy * dy
      if (d2 >= bestD2) continue
      bestD2 = d2
      best = i
    }
  }
  return best
}

/**
 * What the flood fill found, cell by cell (M9-05).
 *
 * The Builder's overlay draws this. **Not a second flood fill** — it is the one that refuses a
 * machine, handed out — because an overlay computed separately would be a picture of a different
 * machine and the author would trust it: it is on screen, and the refusal is a milestone away.
 */
export interface ReachabilityMap {
  readonly cols: number
  readonly rows: number
  /** The bottom-left corner of cell (0,0), in playfield metres. */
  readonly minX: number
  readonly minY: number
  /** The side of one cell, metres. */
  readonly cell: number
  /** 1 where the ball's centre cannot be — inside a wall, a post or a device. */
  readonly blocked: Uint8Array
  /** 1 where a ball put on the machine can get to. */
  readonly reachable: Uint8Array
  /** 1 where it can get to and cannot get back out of. A trap. */
  readonly trapped: Uint8Array
}

function checkFlood(
  def: MachineDefinition,
  solids: readonly Solid[],
  issues: GeometryIssue[],
): ReachabilityMap | undefined {
  const grid = buildGrid(def, solids)
  const planeId = def.layers.find((l) => l.kind === 'plane')?.id

  const gates: GateEdge[] = []
  const teleports: Teleport[] = []
  const seeds: number[] = []
  const escapes: number[] = []

  for (const d of def.devices) {
    if (d.layer !== undefined && d.layer !== planeId) continue
    if (d.kind === 'gate' || d.kind === 'ballSaveGate' || d.kind === 'diverter') {
      const len = sqrt(d.passDirection[0] ** 2 + d.passDirection[1] ** 2)
      if (len === 0) continue
      gates.push({
        ax: d.a[0], ay: d.a[1], bx: d.b[0], by: d.b[1],
        px: d.passDirection[0] / len, py: d.passDirection[1] / len,
      })
    } else if (d.kind === 'kicker' || d.kind === 'plunger' || d.kind === 'autoPlunger') {
      const landing = ejectLanding(grid, d.centre, d.exit.direction, d.captureRadius)
      if (landing < 0) {
        issues.push({
          path: `devices[${def.devices.indexOf(d)}]`,
          code: 'eject-blocked',
          message:
            `"${d.id}" fires the ball into a wall: nothing along its exit direction is clear of ` +
            `its own ${(d.captureRadius * 1000).toFixed(0)} mm capture radius.`,
          severity: 'error',
        })
      } else {
        teleports.push({ fromX: d.centre[0], fromY: d.centre[1], radius: d.captureRadius, toCell: landing })
      }
      if (d.kind === 'plunger' || d.kind === 'autoPlunger') {
        const at = nearestFree(grid, d.centre[0], d.centre[1], BALL_DIAMETER)
        if (at >= 0) seeds.push(at)
      }
    }
  }

  // A ramp entry is a way off the plane, and a ramp or wire exit is a way back on to it.
  for (const layer of def.layers) {
    if (layer.kind === 'ramp') {
      const at = nearestFree(grid, layer.entry.at[0], layer.entry.at[1], layer.width)
      if (at >= 0) escapes.push(at)
    }
    if ((layer.kind === 'ramp' || layer.kind === 'wire') && layer.exit.at !== undefined) {
      const at = nearestFree(grid, layer.exit.at[0], layer.exit.at[1], BALL_DIAMETER * 2)
      if (at >= 0) seeds.push(at)
    }
  }

  // The drain: every free cell along the bottom edge of the outline.
  for (let col = 0; col < grid.cols; col++) {
    if (grid.blocked[col] === 0) escapes.push(col)
  }

  if (seeds.length === 0) {
    issues.push({
      path: 'devices',
      code: 'no-ball-entry',
      message: 'no plunger, auto-plunger or ramp exit puts a ball on the playfield, so nothing can be reached',
      severity: 'error',
    })
    return undefined
  }
  if (escapes.length === 0) {
    issues.push({
      path: 'colliders',
      code: 'no-drain',
      message: 'the bottom of the playfield is sealed, so a ball can never leave and the ball can never end',
      severity: 'error',
    })
    return undefined
  }

  const reachable = fill(grid, seeds, gates, teleports, false)
  const canEscape = fill(grid, escapes, gates, teleports, true)

  // A trap is somewhere the ball gets to and cannot get out of. Reported as the largest such
  // pocket with a coordinate, because "there is a trap somewhere" is not actionable.
  const trapped = new Uint8Array(grid.cols * grid.rows)
  let trappedCells = 0
  for (let i = 0; i < trapped.length; i++) {
    if (reachable[i] === 1 && canEscape[i] === 0) {
      trapped[i] = 1
      trappedCells++
    }
  }
  if (trappedCells > 0) {
    const pocket = largestPocket(grid, trapped)
    const area = pocket.size * CELL * CELL * 1e4
    issues.push({
      path: 'colliders',
      code: 'ball-trap',
      message:
        `a ball can reach (${pocket.x.toFixed(4)}, ${pocket.y.toFixed(4)}) and has no way back out. ` +
        `The pocket covers ${area.toFixed(1)} cm²; ${trappedCells} of ${countFree(grid)} reachable ` +
        'cells are dead ends. Either open the pocket, point a gate the other way, or put a coil in it.',
      severity: 'error',
    })
  }

  for (let i = 0; i < def.devices.length; i++) {
    const d = def.devices[i]!
    if (!isScoringDevice(d.kind)) continue
    if (d.layer !== undefined && d.layer !== planeId) continue
    const [x, y] = devicePosition(d)
    // Generous: the ball only has to get *beside* the device, and a target's own face is solid.
    const at = nearestFree(grid, x, y, BALL_DIAMETER * 2, reachable)
    if (at >= 0) continue
    issues.push({
      path: `devices[${i}]`,
      code: 'device-unreachable',
      message:
        `no ball can reach "${d.id}" at (${x.toFixed(4)}, ${y.toFixed(4)}). ` +
        'It will never score, so either the geometry around it is closed or it is in the wrong place.',
      severity: 'warning',
    })
  }

  return {
    cols: grid.cols,
    rows: grid.rows,
    minX: grid.minX,
    minY: grid.minY,
    cell: CELL,
    blocked: grid.blocked,
    reachable,
    trapped,
  }
}

/**
 * Everything the geometry pass found, including the picture it found it in (M9-05).
 *
 * **One pass, both answers.** The Builder wants the complaints *and* the grid to shade, and asking
 * for them separately would rasterise the playfield twice per keystroke to produce two copies of
 * the same fill. The map is `undefined` when the fill could not run at all — no way on to the
 * playfield, or no way off it — and both of those are already errors in the list: on a machine the
 * ball cannot be put on, "where can the ball get to" has no answer, and shading everything would be
 * a confident wrong one.
 */
export function analyseWithReachability(def: MachineDefinition): {
  readonly issues: readonly GeometryIssue[]
  readonly map: ReachabilityMap | undefined
} {
  const issues: GeometryIssue[] = []
  const solids = gatherSolids(def)
  checkOverlap(solids, issues)
  checkFlipperPairs(def, solids, issues)
  checkLayers(def, issues)
  const map = checkFlood(def, solids, issues)
  return { issues, map }
}

function countFree(grid: Grid): number {
  let n = 0
  for (let i = 0; i < grid.blocked.length; i++) if (grid.blocked[i] === 0) n++
  return n
}

/** The largest connected component of a mask, and a point inside it. */
function largestPocket(grid: Grid, mask: Uint8Array): { x: number; y: number; size: number } {
  const seen = new Uint8Array(mask.length)
  let best = { x: 0, y: 0, size: 0 }
  for (let start = 0; start < mask.length; start++) {
    if (mask[start] === 0 || seen[start] === 1) continue
    const queue = [start]
    seen[start] = 1
    let size = 0
    let head = 0
    while (head < queue.length) {
      const at = queue[head++]!
      size++
      const col = at % grid.cols
      const row = (at - col) / grid.cols
      for (let d = 0; d < 4; d++) {
        const nc = col + (d === 0 ? 1 : d === 1 ? -1 : 0)
        const nr = row + (d === 2 ? 1 : d === 3 ? -1 : 0)
        if (nc < 0 || nr < 0 || nc >= grid.cols || nr >= grid.rows) continue
        const next = nr * grid.cols + nc
        if (mask[next] === 0 || seen[next] === 1) continue
        seen[next] = 1
        queue.push(next)
      }
    }
    if (size <= best.size) continue
    const col = start % grid.cols
    const row = (start - col) / grid.cols
    best = { x: cellCentreX(grid, col), y: cellCentreY(grid, row), size }
  }
  return best
}

// --- class: layer integrity -----------------------------------------------------------

/**
 * The highest point the ball actually reaches on a ramp, metres.
 *
 * **Sampled, not scanned.** This read the control points and took the tallest, and a Catmull-Rom
 * spline does not stay under its own control points — it overshoots between them, by more the
 * sharper the change in gradient. So a ramp whose points all sat 2 mm under the glass could bulge
 * *through* it, and both of the checks below would pass a machine that jams the ball on its first
 * shot. Found while building M9-04's height profile, which samples the curve with the simulation's
 * own `Path` and disagreed with this function about how tall a ramp was.
 *
 * The cost is one sampled path per ramp in a pass that already walks every collider, and it runs at
 * publish rather than per step.
 */
function splineApex(spline: readonly (readonly [number, number, number])[]): number {
  let apex = spline[0]?.[2] ?? 0
  for (const p of spline) if (p[2] > apex) apex = p[2]
  // Fewer than two points is not a curve; the format validator refuses it and `Path` throws on it.
  if (spline.length < 2) return apex
  const path = new Path(spline, APEX_SAMPLES_PER_SEGMENT)
  for (let i = 0; i < path.zs.length; i++) {
    const z = path.zs[i] as number
    if (z > apex) apex = z
  }
  return apex
}

function checkLayers(def: MachineDefinition, issues: GeometryIssue[]): void {
  const byId = new Map<string, MachineLayer>()
  for (const l of def.layers) byId.set(l.id, l)

  for (let i = 0; i < def.layers.length; i++) {
    const layer = def.layers[i]!
    const path = `layers[${i}]`

    if (layer.kind === 'ramp' || layer.kind === 'wire') {
      // Every ramp and habitrail has to lead somewhere that eventually puts the ball back on a
      // plane. A chain of exits that loops, or that ends on another ramp for ever, is a ball
      // that never comes back.
      let at: MachineLayer = layer
      const walked = new Set<string>([layer.id])
      let outcome: 'plane' | 'cycle' | 'dead-end' = 'dead-end'
      for (let hop = 0; hop <= def.layers.length; hop++) {
        if (at.kind !== 'ramp' && at.kind !== 'wire') break
        const next = byId.get(at.exit.to)
        if (next === undefined) break
        if (next.kind === 'plane' || next.kind === 'queue') {
          outcome = 'plane'
          break
        }
        if (walked.has(next.id)) {
          outcome = 'cycle'
          break
        }
        walked.add(next.id)
        at = next
      }
      if (outcome === 'cycle') {
        issues.push({
          path: `${path}.exit.to`,
          code: 'layer-exit-cycle',
          message: `following the exits from "${layer.id}" loops without ever reaching a plane`,
          severity: 'error',
        })
      } else if (outcome === 'dead-end') {
        issues.push({
          path: `${path}.exit.to`,
          code: 'layer-exit-dead-end',
          message: `"${layer.id}" never delivers the ball back to a plane layer`,
          severity: 'error',
        })
      }

      const apex = splineApex(layer.spline)
      const clear = def.dimensions.glassHeight - (apex + BALL_DIAMETER)
      if (clear < 0) {
        issues.push({
          path: `${path}.spline`,
          code: 'layer-hits-glass',
          message:
            `"${layer.id}" tops out at ${apex.toFixed(4)} m; with the ball on it that needs ` +
            `${(apex + BALL_DIAMETER).toFixed(4)} m of headroom and the glass is at ` +
            `${def.dimensions.glassHeight.toFixed(4)} m. The ball would jam against the glass.`,
          severity: 'error',
        })
      }
    }

    if (layer.kind !== 'ramp') continue

    // Climbable at the calibrated shot speed: a rolling ball carries (7/10)·m·v² and has to
    // spend m·g·Δh climbing, with a deliberately pessimistic allowance for what it loses on
    // the way (RAMP_ENERGY_EFFICIENCY).
    const rise = splineApex(layer.spline) - (layer.spline[0]?.[2] ?? 0)
    const budget = RAMP_ENERGY_EFFICIENCY * 0.7 * CALIBRATED_SHOT_SPEED * CALIBRATED_SHOT_SPEED
    if (rise * G > budget) {
      const needed = sqrt((rise * G) / (RAMP_ENERGY_EFFICIENCY * 0.7))
      issues.push({
        path: `${path}.spline`,
        code: 'ramp-not-climbable',
        message:
          `"${layer.id}" rises ${(rise * 1000).toFixed(0)} mm, which needs ${needed.toFixed(2)} m/s at ` +
          `the entry. A full-strength flipper delivers ${CALIBRATED_SHOT_SPEED.toFixed(2)} m/s (PH-A07), ` +
          'so the shot can never be completed.',
        severity: 'error',
      })
    }
  }
}

// --- the pass ---------------------------------------------------------------------------

export interface GeometryOptions {
  /**
   * Run the flood fill. Off by default because it rasterises the whole playfield, which is
   * milliseconds rather than microseconds — fine for the Builder, for a publish check and for
   * the test suite, and not something a client should pay on every load of a machine the
   * server already validated.
   */
  readonly flood?: boolean
}

/**
 * Run the geometry classes of 04 §16 over a definition that has already passed `validate.ts`.
 *
 * Order is fixed and results are appended in it, so two runs over the same document produce
 * the same report in the same sequence.
 */
export function analyseGeometry(def: MachineDefinition, options: GeometryOptions = {}): GeometryIssue[] {
  const issues: GeometryIssue[] = []
  const solids = gatherSolids(def)
  checkOverlap(solids, issues)
  checkFlipperPairs(def, solids, issues)
  checkLayers(def, issues)
  if (options.flood === true) checkFlood(def, solids, issues)
  return issues
}

export { CELL as FLOOD_CELL_SIZE }
